import { useState, useCallback } from 'react';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';

interface DetailsResponse {
  insuranceApplication: {
    attributes: {
      underwritingConsent: {
        givenAt: string | null;
      };
    };
  };
}

interface UseUnderwritingConsentResult {
  content: string | null | undefined;
  isLoading: boolean;
  error: string | null;
  isAlreadyCompleted: boolean;
  fetch: () => Promise<void>;
  accept: () => Promise<void>;
}

export const useUnderwritingConsent = (
  appId: string
): UseUnderwritingConsentResult => {
  const [content, setContent] = useState<string | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAlreadyCompleted, setIsAlreadyCompleted] = useState(false);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [dictionaryResponse, detailsResponse] = await Promise.all([
        httpCheckoutAPI.get({
          url: `/bindapi/insurance_applications/${appId}/dictionary`,
        }),
        httpCheckoutAPI.get({
          url: `/bindapi/insurance_applications/${appId}/details`,
        }),
      ]);

      const markdown =
        dictionaryResponse.data?.underwritingConsent?.markdown ?? null;
      setContent(markdown);

      const givenAt =
        (detailsResponse.data as DetailsResponse)?.insuranceApplication
          ?.attributes?.underwritingConsent?.givenAt ?? null;
      setIsAlreadyCompleted(givenAt !== null);
    } catch {
      setError('Unable to load consent information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [appId]);

  const accept = useCallback(async (): Promise<void> => {
    try {
      await httpCheckoutAPI.post({
        url: `/bindapi/insurance_applications/${appId}/underwriting_consent/complete`,
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) throw err;
    }
  }, [appId]);

  return {
    content,
    isLoading,
    error,
    isAlreadyCompleted,
    fetch,
    accept,
  };
};
